<script lang="ts">
  import '$lib/styles/blog.css';
  import { blogPosts } from '$lib/data/blog-posts';

  const title = 'Security for autonomous systems';
  const description =
    'Research on agent authority, attack paths and the evidence needed to grant more autonomy.';
  const featured = blogPosts.find((post) => post.featured);
  const allPosts = [...blogPosts].sort(
    (a, b) => Date.parse(b.date) - Date.parse(a.date),
  );
  const sections = [
    {
      name: 'Agent security',
      id: 'agent-security',
      description:
        'Agents can chain permitted actions into dangerous outcomes. Our research examines mandates, oversight and the evidence for granting more authority.',
    },
    {
      name: 'Attack research',
      id: 'attack-research',
      description:
        'Azimuth maps attack paths and validates exploits through execution. This work provides the foundation for AGIS.',
    },
  ];
  const archive = allPosts.filter((post) => post.section === 'Archive');
</script>

<svelte:head>
  <title>{title} — TestMachine Blog</title>
  <meta name="description" content={description} />

  <!-- Open Graph meta tags -->
  <meta property="og:title" content="{title} — TestMachine Blog" />
  <meta property="og:description" content={description} />
  <meta
    property="og:image"
    content="https://testmachine.ai/testmachine-og-logo.png"
  />
  <meta property="og:url" content="https://testmachine.ai/blog" />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="TestMachine" />

  <!-- Twitter Card meta tags -->
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="{title} — TestMachine Blog" />
  <meta name="twitter:description" content={description} />
  <meta
    name="twitter:image"
    content="https://testmachine.ai/testmachine-og-logo.png"
  />
</svelte:head>

<section class="blog-hero">
  <div class="container">
    <p class="section-label">TestMachine Blog</p>
    <h1 class="blog-hero-title">
      Security for <span>autonomous systems.</span>
    </h1>
    <p class="blog-hero-desc">{description}</p>
    <nav class="blog-topics" aria-label="Blog topics">
      <a href="#agent-security">Agent security &darr;</a>
      <a href="#attack-research">Attack research &darr;</a>
      <a href="#archive">Archive &darr;</a>
    </nav>
  </div>
</section>

{#if featured}
  <section class="blog-featured-section" aria-labelledby="featured-title">
    <div class="container">
      <article class="blog-featured">
        <p class="section-label">Featured article</p>
        <h2 id="featured-title" class="blog-featured-title">
          <a href="/blog/{featured.slug}">{featured.title}</a>
        </h2>
        <p class="blog-featured-excerpt">{@html featured.excerpt}</p>
        <div class="blog-featured-footer">
          <a href="/blog/{featured.slug}" class="btn btn--primary"
            >Read article &rarr;</a
          >
          <span class="blog-card-date">{featured.date}</span>
        </div>
      </article>
    </div>
  </section>
{/if}

{#each sections as section}
  {@const posts = allPosts.filter(
    (post) => post.section === section.name && post !== featured,
  )}
  <section
    id={section.id}
    class="blog-grid-section"
    aria-labelledby="{section.id}-title"
  >
    <div class="container">
      <div class="blog-section-heading">
        <h2 id="{section.id}-title">{section.name}</h2>
        <p>{section.description}</p>
      </div>
      <div class="blog-grid">
        {#each posts as post}
          <article class="blog-card">
            <div class="blog-card-meta">
              <span class="blog-card-tag">{post.tag}</span>
              <span class="blog-card-date">{post.date}</span>
            </div>
            <h3 class="blog-card-title">
              <a href="/blog/{post.slug}">{post.title}</a>
            </h3>
            <p class="blog-card-excerpt">{@html post.excerpt}</p>
            <a
              href="/blog/{post.slug}"
              class="blog-card-link"
              aria-label="Read {post.title}">Read article &rarr;</a
            >
          </article>
        {/each}
      </div>
    </div>
  </section>
{/each}

<section id="archive" class="blog-archive-section" aria-label="Blog archive">
  <div class="container">
    <details class="blog-archive">
      <summary>Archive <span>{archive.length} articles</span></summary>
      <p class="blog-archive-desc">
        Earlier comparisons, product announcements and company news. These
        articles retain their original publication dates and may describe
        earlier product positioning.
      </p>
      <ul class="blog-archive-list">
        {#each archive as post}
          <li>
            <span class="blog-card-date">{post.date}</span>
            <a href="/blog/{post.slug}">{post.title} &rarr;</a>
          </li>
        {/each}
      </ul>
    </details>
  </div>
</section>
